import { useState, type ReactElement } from "react";
import { Input } from "../../../../shared/components/ui/input";
import { Label } from "../../../../shared/components/ui/label";
import type { CrewMember } from "../../hooks/useSubmissionMutations";
import { CastList } from "./CastList";
import { CrewList } from "./CrewList";

interface CreditsStepProps {
  formData: {
    director?: string;
    cast?: string[];
    crew?: CrewMember[];
  };
  onChange: (field: string, value: string | string[] | CrewMember[]) => void;
}

/**
 * Credits Step - Director, Cast, and Crew
 */
// eslint-disable-next-line max-lines-per-function
export function CreditsStep({ formData, onChange }: Readonly<CreditsStepProps>): ReactElement {
  const [castInput, setCastInput] = useState("");
  const [crewName, setCrewName] = useState("");
  const [crewJob, setCrewJob] = useState("");

  const addCastMember = () => {
    if (!castInput.trim()) return;
    const currentCast = formData.cast || [];
    onChange("cast", [...currentCast, castInput.trim()]);
    setCastInput("");
  };

  const removeCastMember = (index: number) => {
    const currentCast = formData.cast || [];
    onChange(
      "cast",
      currentCast.filter((_, i) => i !== index),
    );
  };

  const addCrewMember = () => {
    if (!crewName.trim() || !crewJob.trim()) return;
    const currentCrew = formData.crew || [];
    onChange("crew", [...currentCrew, { name: crewName.trim(), job: crewJob.trim() }]);
    setCrewName("");
    setCrewJob("");
  };

  const removeCrewMember = (index: number) => {
    const currentCrew = formData.crew || [];
    onChange(
      "crew",
      currentCrew.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Credits</h2>
        <p className="text-sm text-neutral-400 pb-6">Add the key people involved in your film's creation.</p>
      </div>

      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        {/* Director */}
        <div className="space-y-2">
          <Label htmlFor="director">Director</Label>
          <Input
            type="text"
            id="director"
            placeholder="Director's name"
            value={formData.director || ""}
            onChange={(e) => onChange("director", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-5 md:grid-cols-5 gap-8">
          {/* Cast */}
          <div className="space-y-2 col-span-2">
            <Label>Cast Members</Label>
            <p className="text-xs text-neutral-500 pb-4">Add one at a time</p>

            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Actor's name"
                value={castInput}
                onChange={(e) => setCastInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCastMember();
                  }
                }}
              />
              <button
                type="button"
                onClick={addCastMember}
                disabled={!castInput.trim()}
                className="btn-admin-action disabled:cursor-not-allowed whitespace-nowrap"
              >
                +
              </button>
            </div>

            {formData.cast && formData.cast.length > 0 && <CastList cast={formData.cast} onRemove={removeCastMember} />}
          </div>

          {/* Crew */}
          <div className="space-y-2 col-span-3">
            <Label>Crew Members</Label>
            <p className="text-xs text-neutral-500 pb-4">
              Add key crew with their roles (e.g., Cinematographer, Producer)
            </p>

            <div className="grid grid-cols-[1fr,auto,auto] gap-2">
              <Input
                type="text"
                placeholder="Name"
                value={crewName}
                onChange={(e) => setCrewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCrewMember();
                  }
                }}
              />
              <Input
                type="text"
                placeholder="Role"
                value={crewJob}
                onChange={(e) => setCrewJob(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCrewMember();
                  }
                }}
                className="w-40"
              />
              <button
                type="button"
                onClick={addCrewMember}
                disabled={!crewName.trim() || !crewJob.trim()}
                className="btn-admin-action disabled:cursor-not-allowed whitespace-nowrap"
              >
                +
              </button>
            </div>

            {formData.crew && formData.crew.length > 0 && <CrewList crew={formData.crew} onRemove={removeCrewMember} />}
          </div>
        </div>
      </form>
    </div>
  );
}
